import React, { useEffect, useRef, useState } from 'react';
import { SignInAuthChallenge } from '@lambda/apis/src/customer/cognito/enum';
import { CtaButton } from '@/components/Cta/Cta';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { AUTH_STEPS, OTP_LENGTH } from '../../constants';
import { OtpVerificationStepProps } from '../types';
import i18n from '@/settings/i18n';
import { translateBackendError } from '@/lib/formatStatusCodeError';

const t = i18n.init('auth');

const OtpVerificationStep = ({
  setStep,
  data,
  setData,
  onSuccess,
}: OtpVerificationStepProps) => {
  const { cognitoLogin, cognitoVerifyOtp, cognitoResendOtp, loading } =
    useCustomerContext();
  const [error, setError] = useState('');
  const otpRequested = useRef(false);

  // Password-first flow no longer sends the OTP at the email step, so request
  // it when this backup step is entered (guarded against double-send).
  useEffect(() => {
    if (otpRequested.current || !data.email) {
      return;
    }
    otpRequested.current = true;

    cognitoLogin(data.email, undefined, SignInAuthChallenge.EMAIL_OTP)
      .then((res) => {
        if (res?.error) {
          setError(
            translateBackendError(
              res.message,
              t`Something went wrong while sending the code.`,
            ),
          );
        }
      })
      .catch(() => {
        setError(t`Something went wrong while sending the code.`);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSignInClick = async () => {
    try {
      const auth = await cognitoVerifyOtp(data.email, data.otp);

      if (!auth?.error) {
        onSuccess();
      } else {
        setError(
          translateBackendError(
            auth?.message,
            t`Invalid or expired OTP. Request a new one.`,
          ),
        );
      }
    } catch (e) {
      setError(t`Something went wrong while verifying the OTP.`);
    }
  };

  const onResendClick = async () => {
    try {
      const customer = await cognitoResendOtp(data.email);

      if (!customer.error) {
        setStep(AUTH_STEPS.OTP_VERIFICATION);
      } else {
        setError(
          translateBackendError(
            customer?.message,
            t`Something went wrong while resending the OTP.`,
          ),
        );
      }
    } catch (e) {
      setError(t`Something went wrong while resending the OTP.`);
    }
  };

  return (
    <React.Fragment>
      <div className="flex flex-wrap items-center justify-between gap-x-2">
        <div>
          {t`Sent to ${{ email: data.email }}`}{' '}
          <button
            onClick={() => setStep(AUTH_STEPS.EMAIL)}
            className="underline underline-offset-1"
          >
            {t`Change`}
          </button>
        </div>
        <button
          className="text-[#0D6EFD] underline-offset-1 hover:underline"
          onClick={onResendClick}
        >
          {t`Resend`}
        </button>
      </div>
      <input
        type="text"
        placeholder="12345678"
        className="mt-2.5 h-12 w-full rounded border border-gray-700/10 px-3 text-gray-700 outline-1 outline-gray-700 placeholder:text-gray-700/50"
        value={data.otp}
        onChange={(e) => setData({ ...data, otp: e.target.value })}
        autoComplete="one-time-code"
      />
      <CtaButton
        type="button"
        className="mt-2.5 !h-12 w-full !rounded !text-sm"
        onClick={onSignInClick}
        loading={loading}
        disabled={data.otp.length !== OTP_LENGTH}
      >
        {t`Sign in`}
      </CtaButton>
      {error ? <div className="text-sm text-red">{error}</div> : null}
    </React.Fragment>
  );
};

export default OtpVerificationStep;
